<template>
    <el-container class="fileshare-shell">
        <!-- 内容区域 -->
        <el-main class="fileshare-main">
            <div class="fileshare-page">
                <!-- 上传区 -->
                <section class="fs-hero">
                    <div class="fs-hero__head">
                        <div class="fs-hero__intro">
                            <h2 class="fs-hero__title">共享文件</h2>
                            <p class="fs-hero__desc">上传一次即可生成下载链接，团队成员打开链接就能获取文件</p>
                        </div>
                        <div v-if="isLogin" class="fs-space">
                            <div class="fs-space__top">
                                <span class="fs-space__label">剩余空间</span>
                                <span class="fs-space__value">{{ remainSpace }} <i>MB</i></span>
                            </div>
                            <div class="fs-space__bar">
                                <span class="fs-space__bar-inner" :style="{ width: usedPercent + '%' }"></span>
                            </div>
                        </div>
                    </div>

                    <el-upload
                        v-if="isLogin"
                        class="fs-upload"
                        :action="fileUploadUrl"
                        :data="{ bucketName: '.share' }"
                        :headers="headers"
                        drag
                        multiple
                    >
                        <el-icon class="fs-upload__icon"><upload-filled /></el-icon>
                        <div class="fs-upload__text">将文件拖到此处，或<em>点击选择文件</em></div>
                        <div class="fs-upload__hint">支持一次选择多个文件，上传完成后会出现在下方“最近”列表</div>
                    </el-upload>

                    <el-alert v-else class="fs-alert" type="info" :closable="false" show-icon title="登录后即可上传文件并生成分享链接" />
                </section>

                <!-- 操作步骤 -->
                <section class="fs-steps">
                    <div class="fs-step" v-for="step in steps" :key="step.no">
                        <span class="fs-step__no">{{ step.no }}</span>
                        <div class="fs-step__body">
                            <p class="fs-step__title">{{ step.title }}</p>
                            <p class="fs-step__desc">{{ step.desc }}</p>
                        </div>
                    </div>
                </section>

                <!-- 最近文件 -->
                <section class="fs-list">
                    <div class="fs-list__head">
                        <div class="fs-list__title-wrap">
                            <h3 class="fs-list__title">最近</h3>
                            <span class="fs-list__count">{{ filterShareFileList().length }}</span>
                        </div>
                        <el-input v-model="fileSearchText" class="fs-search" placeholder="输入文件名，即时筛选" clearable>
                            <template #prefix>
                                <el-icon><Search /></el-icon>
                            </template>
                        </el-input>
                    </div>

                    <div class="fs-groups">
                        <section class="fs-group" v-for="group in groupedShareFileList" :key="group.key">
                            <header class="fs-group__head">
                                <el-avatar class="fs-group__avatar" :size="36" shape="square" :src="avatarSrc(group.avatar)">{{ group.realName.substring(0, 1) }}</el-avatar>
                                <div class="fs-group__info">
                                    <p class="fs-group__name">{{ group.realName }}</p>
                                    <p class="fs-group__meta">分享 {{ group.files.length }} 个文件</p>
                                </div>
                            </header>
                            <el-row :gutter="14" class="fs-grid">
                                <el-col v-for="item in group.files" :key="item" :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
                                    <el-card class="fs-card" shadow="never">
                                        <div class="fs-card__top">
                                            <file-type-icon class="fs-card__icon" :name="item.ext" :size="46" />
                                            <div class="fs-card__info">
                                                <div class="fs-card__name" :title="item.fileName">{{ item.fileName }}</div>
                                                <div class="fs-card__meta">
                                                    <span>{{ parseInt(item.size / 1024 / 1024) }} MB</span>
                                                    <i class="fs-dot"></i>
                                                    <span>{{ item.createTime }}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="fs-card__tags">
                                            <span class="fs-tag fs-tag--ghost">查看 {{ item.downloadCount }} 次</span>
                                        </div>

                                        <div class="fs-card__foot">
                                            <span class="fs-card__last">最近查看 {{ item.downloadTime }}</span>
                                            <div class="fs-card__actions">
                                                <el-button class="fs-card__btn" size="small" type="primary" plain @click="fileDownload(item)">
                                                    <el-icon><Download /></el-icon>
                                                    <span>下载</span>
                                                </el-button>
                                                <el-button class="fs-card__btn" size="small" plain @click="fileDownloadCopy(item)">
                                                    <el-icon><Link /></el-icon>
                                                    <span>复制链接</span>
                                                </el-button>
                                            </div>
                                        </div>
                                    </el-card>
                                </el-col>
                            </el-row>
                        </section>
                    </div>

                    <el-empty v-if="filterShareFileList().length === 0" :description="fileSearchText ? '没有匹配的文件' : '暂无共享文件，上传一个试试'" />
                </section>
            </div>
        </el-main>
    </el-container>
</template>

<script>
import { ElMessageBox, ElMessage } from 'element-plus';
import { Search, Share, Guide } from '@element-plus/icons';
import request from '../../utils/request.js';
import { config } from '@/utils/config';
import { getToken } from '@/utils/auth';
import _ from 'lodash';
import { copy } from '../../utils/clipboard.js';
import FileTypeIcon from '../workbench/fileman/FileTypeIcon.vue';
import { avatarImage } from '@/utils/users';
export default {
    data() {
        return {
            headers: [],
            fileUploadUrl: '',
            user: {},
            fileConf: {},
            shareFileList: [],
            shareFileTotal: 0,
            fileSearchText: '',
            steps: [
                { no: 1, title: '上传文件', desc: '拖拽或点击上方区域选择文件' },
                { no: 2, title: '复制链接', desc: '在文件卡片上点击“复制链接”' },
                { no: 3, title: '分享下载', desc: '发送给同事，打开链接即可下载' }
            ]
        };
    },
    components: { FileTypeIcon },
    computed: {
        // 是否已登录：未登录时仅展示提示，不提供上传入口
        isLogin() {
            const token = this.$store.state.user.token;
            return !(token == undefined || token == '');
        },
        // 剩余可用空间（MB）
        remainSpace() {
            return this.fileConf.spaceLimit ? parseInt((this.fileConf.spaceLimit - this.fileConf.usedSpace) / 1024 / 1024) : 0;
        },
        // 空间占用百分比，仅用于展示
        usedPercent() {
            const limit = Number(this.fileConf.spaceLimit) || 0;
            const used = Number(this.fileConf.usedSpace) || 0;
            if (!limit) {
                return 0;
            }
            return Math.min(100, Math.max(0, Math.round((used / limit) * 100)));
        },
        // 按分享人分组：同一个人的文件聚在一块，避免不同来源的文件混在一起
        // 以 userId 作为分组键（改名不影响归组），按列表原有顺序输出
        groupedShareFileList() {
            const groups = [];
            const groupIndex = {};
            this.filterShareFileList().forEach(item => {
                const key = item.userId || item.realName || 'unknown';
                if (!groupIndex[key]) {
                    groupIndex[key] = { key: key, realName: item.realName || '匿名', avatar: item.avatar, files: [] };
                    groups.push(groupIndex[key]);
                }
                groupIndex[key].files.push(item);
            });
            return groups;
        }
    },
    mounted() {
        document.title = 'Hidoc-共享文件';

        this.headers['Access-Token'] = getToken();
        this.fileUploadUrl = config().baseServer + 'f/u';

        var user = this.$store.state.user;
        console.log(user);
        if (user.token) {
            this.getCurrentUserInfo();
        }

        this.getShareFileList();
    },
    methods: {
        // 分享人头像：为空时 avatarImage 会返回默认头像，加载失败则由 el-avatar 回退到首字
        avatarSrc(avatar) {
            return avatarImage(avatar);
        },
        copy(s) {
            copy(s);
            ElMessage({
                message: '复制成功',
                type: 'success',
                duration: 1 * 1000
            });
        },
        getCurrentUserInfo() {
            request({
                url: '/user/currentUserInfo',
                method: 'post'
            }).then(res => {
                if (res.code == 0) {
                    this.user = res.meta.user;
                    this.fileConf = res.meta.fileConf;
                }
            });
        },
        getShareFileList(current) {
            request({
                url: '/openapi/file/shareList',
                method: 'post',
                data: {
                    token: this.$store.state.user.token
                }
            }).then(res => {
                if (res.code == 0) {
                    this.shareFileList = res.data;
                    this.shareFileTotal = res.total;
                }
            });
        },
        filterShareFileList() {
            // 统一转小写比较，使筛选不区分大小写
            const t = (this.fileSearchText || '').toLowerCase();
            return _.filter(this.shareFileList, function (o) {
                return String(o.fileName || '').toLowerCase().indexOf(t) >= 0;
            });
        },
        fileDownload(data) {
            window.location.href = config().baseServer + 'f/d/u/' + data.uname;
        },
        // 生成便于直接粘贴到聊天窗口的分享文本：文件信息在前，下载地址收尾
        buildShareText(data) {
            const sizeMb = data.size ? parseInt(data.size / 1024 / 1024) : 0;
            const details = ['大小：' + sizeMb + ' MB'];
            if (data.realName) {
                details.push('上传者：' + data.realName);
            }
            if (data.createTime) {
                details.push('上传时间：' + data.createTime);
            }
            return ['【Hidoc 共享文件】' + data.fileName, details.join('｜'), '下载链接：' + config().baseServer + 'f/d/u/' + data.uname].join('\n');
        },
        fileDownloadCopy(data) {
            this.copy(this.buildShareText(data));
        }
    }
};
</script>

<style lang="scss" scoped>
.fileshare-shell {
    height: 100%;
}

/* 外层 .app-main-body 已提供内边距与浅色画布，这里清零避免双层留白 */
.fileshare-main {
    height: 100%;
    padding: 0;
}

.fileshare-page {
    /* 与首页、登录页保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --fs-text: #1f2d3d;
    --fs-text-sub: #5b6a80;
    --fs-text-muted: #aab4c4;
    --fs-line: #e6eaf0;
    --fs-line-soft: #eef1f6;
    --fs-radius: 14px;
    --fs-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);

    color: var(--fs-text);
}

/* ---------- 上传区 ---------- */
.fs-hero {
    padding: 20px 22px 22px;
    border: 1px solid var(--fs-line);
    border-radius: var(--fs-radius);
    background:
        radial-gradient(120% 140% at 100% 0%, #eef4ff 0%, rgba(238, 244, 255, 0) 55%),
        #ffffff;
    box-shadow: var(--fs-shadow);
}

.fs-hero__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 16px;
}

.fs-hero__title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

.fs-hero__desc {
    margin: 6px 0 0;
    font-size: 13px;
    color: var(--fs-text-sub);
}

/* 空间用量：细进度条 + 数字 */
.fs-space {
    flex: 0 0 auto;
    width: 170px;
}

.fs-space__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: var(--fs-text-sub);
}

.fs-space__value {
    font-size: 14px;
    font-weight: 700;
    color: var(--fs-text);
}

.fs-space__value i {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    color: var(--fs-text-sub);
}

.fs-space__bar {
    margin-top: 7px;
    height: 6px;
    border-radius: 999px;
    background-color: var(--fs-line-soft);
    overflow: hidden;
}

.fs-space__bar-inner {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #4f86f7 0%, #2f6fed 100%);
    transition: width 0.3s;
}

/* 拖拽上传区 */
.fs-upload :deep(.el-upload) {
    display: block;
}

.fs-upload :deep(.el-upload-dragger) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 26px 18px;
    border: 1px dashed #cfd8e6;
    border-radius: 12px;
    background-color: #fbfcfe;
    transition:
        border-color 0.25s,
        background-color 0.25s;
}

.fs-upload :deep(.el-upload-dragger:hover) {
    border-color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.fs-upload__icon {
    margin-bottom: 10px;
    font-size: 42px;
    color: var(--el-color-primary);
}

.fs-upload__text {
    font-size: 14px;
    color: var(--fs-text-sub);
}

.fs-upload__text em {
    font-style: normal;
    font-weight: 600;
    color: var(--el-color-primary);
}

.fs-upload__hint {
    margin-top: 6px;
    font-size: 12px;
    color: var(--fs-text-muted);
}

.fs-alert {
    border-radius: 12px;
}

/* ---------- 操作步骤 ---------- */
.fs-steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
}

.fs-step {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid var(--fs-line);
    border-radius: 12px;
    background-color: #ffffff;
    box-shadow: var(--fs-shadow);
}

.fs-step__no {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 13px;
    font-weight: 700;
}

.fs-step__body {
    min-width: 0;
}

.fs-step__title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--fs-text);
}

.fs-step__desc {
    margin: 3px 0 0;
    font-size: 12px;
    color: var(--fs-text-sub);
}

/* ---------- 最近文件 ---------- */
.fs-list {
    margin-top: 22px;
}

.fs-list__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
}

.fs-list__title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
}

.fs-list__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
}

.fs-list__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 7px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 600;
}

.fs-search {
    width: 260px;
    max-width: 100%;
}

.fs-search :deep(.el-input__wrapper) {
    border-radius: 10px;
    background-color: #ffffff;
    box-shadow: 0 0 0 1px var(--fs-line) inset;
    transition: box-shadow 0.2s;
}

.fs-search :deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px #cfd8e6 inset;
}

.fs-search :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

/* ---------- 分享人分组 ---------- */
.fs-groups {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.fs-group {
    padding: 16px 18px 4px;
    border: 1px solid var(--fs-line);
    border-radius: 16px;
    background-color: #ffffff;
    box-shadow: var(--fs-shadow);
}

.fs-group__head {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--fs-line-soft);
}

/* 头像：图片正常时显示图片，加载失败/无头像时回退为渐变底 + 姓名首字 */
.fs-group__avatar {
    flex: 0 0 auto;
    border-radius: 11px;
    background: linear-gradient(135deg, #4f86f7 0%, #2f6fed 100%);
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    user-select: none;
}

.fs-group__info {
    min-width: 0;
}

.fs-group__name {
    margin: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    color: var(--fs-text);
}

.fs-group__meta {
    margin: 3px 0 0;
    font-size: 12px;
    color: var(--fs-text-muted);
}

/* ---------- 卡片栅格 ---------- */
.fs-grid :deep(.el-col) {
    margin-bottom: 14px;
}

/* 抵消工作台等页面全局注入的 .el-card{margin:10px} */
/* 卡片嵌在分组容器内部，用浅底弱阴影拉开层级，避免“卡片套卡片”显得厚重 */
.fs-card {
    margin: 0;
    height: 100%;
    border: 1px solid var(--fs-line);
    border-radius: 12px;
    background-color: #fbfcfe;
    box-shadow: none;
    transition:
        box-shadow 0.25s,
        transform 0.25s,
        border-color 0.25s,
        background-color 0.25s;
}

.fs-card:hover {
    border-color: #cfdcef;
    background-color: #ffffff;
    box-shadow: 0 10px 22px -14px rgba(28, 47, 84, 0.35);
    transform: translateY(-2px);
}

.fs-card :deep(.el-card__body) {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
}

.fs-card__top {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
}

.fs-card__icon {
    flex: 0 0 auto;
    margin-top: 2px;
}

.fs-card__info {
    flex: 1 1 auto;
    min-width: 0;
}

.fs-card__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    color: var(--fs-text);
    transition: color 0.2s;
}

.fs-card:hover .fs-card__name {
    color: var(--el-color-primary);
}

.fs-card__meta {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 5px;
    overflow: hidden;
    white-space: nowrap;
    font-size: 12px;
    color: var(--fs-text-sub);
}

.fs-dot {
    flex: 0 0 auto;
    display: inline-block;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: #c8d2e0;
}

.fs-card__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
}

.fs-tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    height: 22px;
    padding: 0 9px;
    overflow: hidden;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.fs-tag--ghost {
    background-color: #f2f5fa;
    color: var(--fs-text-sub);
}

.fs-card__foot {
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid var(--fs-line-soft);
}

.fs-card__last {
    display: block;
    margin-bottom: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    color: var(--fs-text-muted);
}

.fs-card__actions {
    display: flex;
    gap: 8px;
}

.fs-card__btn {
    flex: 1 1 0;
    margin: 0 !important;
    border-radius: 9px;
    font-weight: 500;
}

/* ---------- 移动端适配 ---------- */
@media (max-width: 768px) {
    .fs-hero {
        padding: 16px;
    }

    .fs-hero__head {
        flex-direction: column;
    }

    .fs-space {
        width: 100%;
    }

    .fs-steps {
        grid-template-columns: 1fr;
    }

    .fs-group {
        padding: 14px 12px 4px;
    }

    .fs-list__head {
        flex-direction: column;
        align-items: stretch;
    }

    .fs-search {
        width: 100%;
    }
}
</style>
